import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Zap, Shield, Database, Users } from 'lucide-react';
import GlobeBackground from '../components/GlobeBackground';

/**
 * Three theme variants, each rendering a complete hero section with:
 *   - rotating wireframe globe background (instead of the LiveData card)
 *   - BOND headline and tagline
 *   - smart-info.it inspired stats (Anni esperienza · Indagini · Clienti)
 *   - CTAs aligned with the smart-info.it tone
 */
const VARIANTS = [
  {
    key: 'v1',
    name: 'V1 — Milano Gold',
    subtitle: 'Champagne gold on obsidian · niente rosso · max eleganza',
    palette: {
      bg: '#050505',
      surface: 'rgba(255,255,255,0.03)',
      accent: '#D4AF37',
      accentHover: '#E6C547',
      muted: '#B8941F',
      text: '#F5F5F5',
      subtext: '#A3A3A3',
      border: 'rgba(212,175,55,0.18)',
    },
    globeVariant: 'gold',
  },
  {
    key: 'v2',
    name: 'V2 — Original Red',
    subtitle: 'Rosso #e60000 + navy #06111f · palette BOND originale',
    palette: {
      bg: '#06111f',
      surface: 'rgba(255,255,255,0.04)',
      accent: '#e60000',
      accentHover: '#ff1f1f',
      muted: '#a30000',
      text: '#E5E5E5',
      subtext: '#A3A3A3',
      border: 'rgba(230,0,0,0.25)',
    },
    globeVariant: 'red',
  },
  {
    key: 'v3',
    name: 'V3 — Mix Gold + Ruby',
    subtitle: 'Nero + oro + un solo accento rubino rosso · best of both',
    palette: {
      bg: '#050505',
      surface: 'rgba(255,255,255,0.03)',
      accent: '#D4AF37',
      accentHover: '#E6C547',
      muted: '#B8941F',
      text: '#F5F5F5',
      subtext: '#A3A3A3',
      border: 'rgba(212,175,55,0.18)',
      ruby: '#dc143c',
    },
    globeVariant: 'mix',
  },
];

const STATS = [
  { value: '+10', label: 'Anni di esperienza', icon: Zap },
  { value: '+2.000', label: 'Indagini effettuate', icon: Shield },
  { value: '+100', label: 'Clienti enterprise', icon: Users },
  { value: '6M+', label: 'Aziende analizzate', icon: Database },
];

const CLIENTS = [
  'UbiBanca', 'Mediobanca', 'BNL · BNP Paribas', 'Findomestic',
  'Intrum', 'Net Insurance', 'Air Liquide', 'IW Private Investments',
];

function HeroVariant({ variant, idx }) {
  const p = variant.palette;
  const isRuby = variant.key === 'v3';

  return (
    <section
      className="relative min-h-[100vh] w-full overflow-hidden"
      style={{ background: p.bg, color: p.text }}
      data-testid={`milano-variant-${variant.key}`}
    >
      {/* Top variant badge */}
      <div
        className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-6 lg:px-12 py-4 backdrop-blur-md"
        style={{
          background: 'rgba(0,0,0,0.55)',
          borderBottom: `1px solid ${p.border}`,
        }}
      >
        <div className="flex items-center gap-3">
          <span
            className="font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-1 rounded-sm border"
            style={{ color: p.accent, borderColor: p.border }}
          >
            {variant.name}
          </span>
          <span className="text-[11px]" style={{ color: p.subtext }}>{variant.subtitle}</span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: p.subtext }}>
          variant {idx + 1} / 3
        </span>
      </div>

      {/* Subtle background grid (decorative) */}
      <div
        className="absolute inset-0 z-0 opacity-30"
        style={{
          backgroundImage: `radial-gradient(circle at 75% 50%, ${p.accent}15 0%, transparent 60%)`,
        }}
      />

      {/* Hero content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pt-24 pb-8 min-h-[100vh] flex flex-col justify-center">
        <div className="grid grid-cols-12 gap-8 lg:gap-12 w-full items-center">

          {/* Left content */}
          <div className="col-span-12 lg:col-span-6 space-y-7">

            {/* Overline */}
            <div className="flex items-center gap-2">
              <span
                className="block h-px w-12"
                style={{ background: p.accent }}
              />
              <span
                className="font-mono text-[11px] uppercase tracking-[0.3em]"
                style={{ color: p.accent }}
              >
                Business Intelligence · OSINT
              </span>
            </div>

            {/* Headline */}
            <h1
              className="font-bold leading-[1.05] tracking-tight"
              style={{
                fontSize: 'clamp(2.25rem, 4.2vw, 3.8rem)',
                color: p.text,
              }}
            >
              Informazioni{' '}
              <span style={{ color: p.accent }}>
                strategiche
              </span>
              {isRuby && (
                <span
                  className="inline-block w-3 h-3 rounded-full mx-1 align-middle"
                  style={{ background: p.ruby, boxShadow: `0 0 18px ${p.ruby}` }}
                />
              )}
              <br />
              per chi decide.
            </h1>

            {/* Tagline */}
            <p
              className="text-base leading-relaxed max-w-xl"
              style={{ color: p.subtext }}
            >
              <strong style={{ color: p.text, fontWeight: 500 }}>BOND</strong>
              {' '}— Piattaforma di Business Intelligence di <em>Smart Info</em> per aziende, banche, studi legali e agenzie investigative:
              ricerche, due diligence, AML/KYC, monitoraggio continuo.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.2em] rounded-sm transition-all"
                style={{
                  background: p.accent,
                  color: variant.key === 'v2' ? '#fff' : '#050505',
                  boxShadow: `0 0 24px ${p.accent}40`,
                }}
                data-testid={`${variant.key}-cta-primary`}
              >
                Richiedi accesso
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </button>
              <button
                className="inline-flex items-center justify-center px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.2em] rounded-sm border transition-all hover:bg-white/5"
                style={{
                  borderColor: p.border,
                  color: p.text,
                }}
              >
                Parla con noi
              </button>
            </div>

            {/* Stats row inspired by smart-info.it */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t" style={{ borderColor: p.border }}>
              {STATS.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div key={stat.label} className="space-y-1">
                    <Icon size={14} style={{ color: p.accent }} />
                    <div className="font-mono font-bold text-xl" style={{ color: p.text }}>
                      {stat.value}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider" style={{ color: p.subtext }}>
                      {stat.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right side — THE GLOBE is the protagonist here */}
          <div className="col-span-12 lg:col-span-6 relative">
            <div
              className="relative aspect-square w-full max-w-[640px] mx-auto"
              data-testid={`${variant.key}-globe-container`}
            >
              <GlobeBackground variant={variant.globeVariant} className="rounded-full" />

              {/* Floating glass label top-left of the globe */}
              <div
                className="absolute top-4 left-4 backdrop-blur-md px-3 py-2 rounded-sm border"
                style={{ background: p.surface, borderColor: p.border }}
              >
                <div className="font-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: p.accent }}>
                  Live network
                </div>
                <div className="font-mono font-bold text-lg" style={{ color: p.text }}>
                  4.092
                </div>
                <div className="text-[10px]" style={{ color: p.subtext }}>nodi attivi</div>
              </div>
            </div>
          </div>
        </div>

        {/* Compact trusted-by strip INSIDE the hero viewport */}
        <div className="mt-10 pt-5 border-t" style={{ borderColor: p.border }}>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            <div className="flex items-center gap-3">
              <span className="block h-px w-6" style={{ background: p.accent }} />
              <span
                className="font-mono text-[10px] uppercase tracking-[0.3em]"
                style={{ color: p.subtext }}
              >
                Scelti da
              </span>
            </div>
            {CLIENTS.map((c) => (
              <span
                key={c}
                className="text-[11px] font-medium tracking-wide opacity-70 hover:opacity-100 transition-opacity"
                style={{ color: p.text }}
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function MilanoPreviewPage() {
  const [searchParams] = useSearchParams();
  const single = searchParams.get('variant'); // "v1" | "v2" | "v3" or null
  const list = single ? VARIANTS.filter(v => v.key === single) : VARIANTS;

  return (
    <div className="bg-black min-h-screen">
      {/* Compact top navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-3 bg-black/70 backdrop-blur-lg border-b border-white/10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[12px] text-white/70 hover:text-white transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft size={14} />
          Torna alla home
        </Link>
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/50">
          Milano Preview {single ? `· ${single.toUpperCase()}` : '· 3 variants'}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-white/50">
          <Link to="/milano-preview?variant=v1" className="hover:text-[#D4AF37] transition-colors">Gold</Link>
          <span className="opacity-30">·</span>
          <Link to="/milano-preview?variant=v2" className="hover:text-[#e60000] transition-colors">Red</Link>
          <span className="opacity-30">·</span>
          <Link to="/milano-preview?variant=v3" className="hover:text-[#D4AF37] transition-colors">Mix</Link>
          <span className="opacity-30 ml-2">·</span>
          <Link to="/milano-preview" className="hover:text-white transition-colors ml-2">All</Link>
        </div>
      </div>

      {list.map((v, i) => (
        <div key={v.key} id={v.key}>
          <HeroVariant variant={v} idx={VARIANTS.indexOf(v)} />
        </div>
      ))}
    </div>
  );
}
